// ════════════════════════════════════════════════════════════════════════════
// BOT ENGINE
// Generic turn driver + decision dispatch for "Play Against Bot" matches.
// Deck-specific behavior lives entirely in js/bot_logic/decks/*.js (see
// registry.js for the registerBotDeck contract) — this file only knows how
// to drive a turn and call into whichever deck is active via
// window.BotDecks[state.botDeckId]. No bot decision-making lives in game.js;
// game.js only exposes the few integration points documented below.
//
// WHY THIS CAN'T JUST REPLAY dispatch('PLAY_CARD'/'DEPLOY_BASE'/'PAIR_PILOT'/
// 'ACTIVATE_BASE', ...) OR triggerCardEffect(card, 'deploy'/'main'/'pair'/...)
// FOR THE BOT'S OWN CARDS:
// This game is built for exactly two real clients, each authoritative for
// "their own" half of the state (state.playerField/hand/tempDeck = mine,
// state.oppField/... = a mirror of the peer's). Every ACTIVE effect handler
// (deploy/main/action/activate/pair) in js/effects/*.js is written assuming
// it only ever runs on the activating player's OWN client, because that's
// the only place it ever actually runs in real P2P play — the peer just
// receives the finished result over the wire. Confirmed directly: ST11-001
// Char's Z'Gok's registered `deploy` handler (js/effects/blue.js) reads
// state.playerField for "another friendly (Marine) Unit" and state.oppField
// for bounce candidates. That's correct when a HUMAN plays it (their own
// card, their own client) — but exactly backwards for a bot-owned copy
// sitting in oppField, where "friendly" means oppField and "enemy" means
// playerField. Every one of Deck 01's active effects (GD01-008 Guntank,
// GD03-056 Barbatos Adapt, ST11-001 Z'Gok, ST01-010 Amuro Ray, ST11-011 Char
// Aznable, ST01-015 White Base, ST02-016 Corsica Base, GD01-100 A Show of
// Resolve) has this same shape. So every one of them gets a deliberate,
// hand-written MIRROR implementation below/in the deck file — built out of
// the low-level primitives that genuinely ARE side-agnostic already
// (dealDamageToUnit, setRested, applyApMod, cardOwnerSide) — rather than
// reusing the registered player-side handler. This mirrors the exact
// convention js/network.js's remoteHandlers.UNREST_ALL/CLEAR_TURN_EFFECTS
// already use for opponent-side mirroring in real networked play (including
// that same file's own comment acknowledging this is a second, independent
// implementation that must be kept in sync by hand, not a refactor).
//
// ATTACK is the one exception: dispatch('ATTACK', {..., mine:false}) already
// threads a side-agnostic `mine` flag through the REAL combat engine (see
// js/network.js's remoteHandlers.ATTACK, which calls exactly this to replay
// a mirrored peer's attack) — so bot attacks reuse it directly and get full
// rules correctness for free, including the human's own normal block-prompt
// UI when the bot attacks them (runBlockStep's ctx.defenderSide === 'player'
// branch already fires correctly with no changes needed).
//
// 'destroyed' IS a genuine exception to "deploy/main/action/activate/pair
// never reach triggerCardEffect for a bot card" above — it fires from
// runDamageStep (and a few other destroy paths) completely unconditionally,
// for EITHER side's card, as plain combat resolution — there's no
// attackerSide/defenderSide gate the way 'attack'/'defended'/'unrest' have.
// But "the call site is symmetric" does NOT mean "every registered handler
// body is side-agnostic" — confirmed the hard way: GD05-050 Gundam Exia
// Repair's own `destroyed` handler (js/effects/purple.js) explicitly
// early-returns unless ctx.ownerSide === 'player', because its effect (mill
// 2 cards) touches state.tempDeck/playerTrash directly, data that's only
// ever real on the local human's own client — it was simply never written
// expecting to run for a mirrored opponent card at all. game.js's
// triggerCardEffect() has its own small redirect for this (see its header
// comment) to window.BotDecks[...].onDestroyed[cardNo] instead of the real
// handler. Any future deck should assume the SAME landmine exists for other
// 'destroyed' handlers too — check the real js/effects/*.js body before
// assuming it's safe to let fire naturally for a bot-owned card, and add an
// onDestroyed entry mirroring it (oppTempDeck/oppTrash/oppHand instead of
// tempDeck/playerTrash/hand) whenever it isn't.
//
// Integration points this file assumes exist in game.js (see the plan this
// feature was built from for exact line references):
//   - state.isBotMatch / state.oppHand / state.oppTempDeck / state.botDeckId
//   - isBotControlledCard(card) + the showCardPickerModal/Multi guards that
//     call BotEngine.resolvePick/resolvePickMulti instead of opening UI
//   - runBlockStep's "else if (state.isBotMatch) BotEngine.decideBlock(...)"
//     branch (human attacks the bot — the bot decides whether to block)
//   - triggerCardEffect's bot-owned + trigger==='destroyed' redirect to
//     window.BotDecks[...].onDestroyed[cardNo] (see above)
//   - getValidAttackTargets(attacker, attackerSide) accepting 'opp'
//   - resolveShieldHit('opp', count) for the bot's own (real) shields
//   - expandBotDecklist(decklist) / showBotDeckSelectScreen / the main menu
//     "Play Against Bot" wiring (pure UI, lives in game.js alongside the
//     human's existing showDeckSelectScreen/showMatchmakingScreen)
// ════════════════════════════════════════════════════════════════════════════
window.BotEngine = (function () {

  function activeDeck() {
    return state.botDeckId ? window.BotDecks[state.botDeckId] : null;
  }

  // True once the match has ended (e.g. an earlier direct attack THIS SAME
  // turn already defeated the human) — checked between steps of the attack/
  // deploy loops so the bot doesn't keep attacking/deploying/ending a turn
  // against a match that's already over. A human player naturally stops the
  // moment they see this modal; the bot's own loops have no such pause.
  function isMatchOver() {
    return !!document.getElementById('victory-overlay');
  }

  // True while the human has a decision-blocking modal open — a picker
  // (showCardPickerModal/Multi, showHandDiscardModal, showActionStepModal,
  // showUnitActivateModal, showBaseActivateModal — all built on the same
  // #ingame-menu-overlay shell), or the manual pilot-pairing confirm. The
  // bot's own setTimeout-paced loops have no natural reason to wait for
  // these the way a real second client would (a real peer simply wouldn't
  // send its next move until its own player acted) — without this check,
  // the bot kept firing new deploys/attacks on its fixed schedule while the
  // human still had an unanswered Action Step picker open, stacking a new
  // modal on top of it instead of waiting.
  function isHumanModalOpen() {
    return !!(document.getElementById('ingame-menu-overlay') ||
              document.getElementById('pair-confirm-overlay'));
  }

  // Runs `cb` once the match isn't over, no human modal is open, and no
  // attack is mid-resolution — polling rather than a fixed delay, since a
  // human's own response time (reading an Action Step picker, deciding
  // whether to block) is inherently unbounded. Every bot action loop below
  // routes its pacing through this instead of a bare setTimeout.
  function waitUntilClearToAct(cb) {
    const poll = () => {
      if (isMatchOver()) return;
      if (isHumanModalOpen() || state.attackInProgress) { setTimeout(poll, 250); return; }
      cb();
    };
    poll();
  }

  // Paces one bot action: waits `ms`, THEN waits until clear to act (see
  // waitUntilClearToAct) before actually calling `cb` — so a modal that
  // opens partway through the pacing delay still gets waited out.
  function botDelay(ms, cb) {
    setTimeout(() => waitUntilClearToAct(cb), ms);
  }

  function botName() {
    return activeDeck()?.name || 'Bot';
  }

  // Snapshot handed to every deck hook. Decks are free to read state.* fields
  // directly (same global-scope convention as every other file here), but
  // most of what a deck's decision logic needs is pre-resolved here so deck
  // files read like game rules, not state plumbing.
  function buildBotCtx() {
    return {
      hand: state.oppHand,
      field: state.oppField,
      base: state.oppBase,
      shield: state.oppShield,
      res: state.oppRes,
      exRes: state.oppExRes,
      resBase: state.oppResBase,
      level: state.oppLevel,
      trash: state.oppTrash,
      enemyField: state.playerField,
      enemyBase: state.playerBase,
      enemyShield: state.playerShield,
      enemyHand: state.hand,
      turn: state.turn,
      canPlayCard: botCanPlayCard,
      findEmptySlot: botFindEmptyOppSlot,
    };
  }

  // ──────────────────────────────────────────────────────────────────────────
  // SETUP / MULLIGAN — called once from initGame()'s bot branch, before the
  // human's own mulligan modal opens. Resolving the bot's mulligan decision
  // synchronously here (rather than on some later tick) is what lets
  // finishMulligan()'s existing `else { mulliganRemoteDone = true; }` branch
  // (game.js) stay correct with no edit of its own — by the time that runs,
  // the bot's hand is already final.
  // ──────────────────────────────────────────────────────────────────────────
  function setupBotSide(botDeckDef) {
    state.botDeckId = botDeckDef.id;
    state.isBotMatch = true;
    state.oppLevel = 0; // overrides the generic networked-placeholder default (4) — no COUNTER_SYNC will ever correct it here
    state.oppTempDeck = buildTempDeck(expandBotDecklist(botDeckDef.decklist));
    state.oppDeckCount = state.oppTempDeck.length;

    state.oppHand = botDrawCards(5);
    state.oppHandCount = state.oppHand.length;

    const deck = activeDeck();
    if (deck?.shouldMulligan?.(state.oppHand)) {
      addLog(`${deck.name} — mulligans its opening hand.`, { noPill: true });
      state.oppHand.forEach(c => {
        const entry = state.oppTempDeck.find(e => e.cardNo === c.cardNo && e.copyVer === c.copyVer);
        if (!entry) return;
        const idx = state.oppTempDeck.indexOf(entry);
        state.oppTempDeck.splice(idx, 1);
        entry.state = 'deck';
        state.oppTempDeck.push(entry);
      });
      state.oppHand = botDrawCards(5);
      state.oppHandCount = state.oppHand.length;
      state.oppTempDeck = shuffleDeck(state.oppTempDeck);
    }
    dealOppShields(6);

    // Catch-up bonus — whoever goes second starts with 1 EX Resource (see
    // initGame()'s identical `if (!state.myTurn) { state.exRes = 1; }` for
    // the networked/human side, game.js — that check reads state.myTurn
    // from the HUMAN's own perspective, so "not my turn" there means "I'm
    // going second". Mirrored here means the OPPOSITE polarity: the bot
    // goes second exactly when state.myTurn IS true (the human was assigned
    // the first turn) — initGame() hardcodes state.myTurn true for every
    // non-networked match today, so this is unconditionally the bot's case
    // right now, but written as a real check (not just an unconditional
    // assignment) so it keeps working correctly if a future change ever
    // lets the bot go first instead. CONFIRMED BUG in an earlier version of
    // this fix: it checked `!state.myTurn` (copy-pasted the human-side
    // polarity directly) — always false here, since state.myTurn is always
    // true for a bot match, so the bonus silently never applied at all.
    if (state.myTurn) {
      state.oppExRes = 1;
      recomputeOppLevel();
      addLog('Going second — gained 1 EX Resource.', { noPill: true });
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  // DECK / RESOURCE / HAND PRIMITIVES — bot-side mirrors of game.js's
  // player-only buildTempDeck/drawCardsFromTempDeck-family helpers. None of
  // those are reusable as-is (every one of them hardcodes state.tempDeck/
  // state.hand), so these are deliberate parallels, same convention as the
  // header comment above describes for active card effects.
  // ──────────────────────────────────────────────────────────────────────────
  function botDrawCards(count) {
    const drawn = [];
    if (!state.oppTempDeck) return drawn;
    const available = state.oppTempDeck.filter(isInDeck);
    for (let i = 0; i < count && available.length > 0; i++) {
      const entry = available.splice(0, 1)[0];
      const dbEntry = cardDb[entry.cardNo];
      if (!dbEntry) { i--; continue; }
      entry.state = 'hand';
      drawn.push(buildGameCardFromDb(dbEntry, entry.copyVer));
    }
    state.oppDeckCount = state.oppTempDeck.filter(isInDeck).length;
    return drawn;
  }

  function dealOppShields(count) {
    if (!state.oppTempDeck) { state.oppShield = count; return; }
    const available = state.oppTempDeck.filter(isInDeck).slice(0, count);
    available.forEach(entry => { entry.state = 'shield'; });
    state.oppShield = available.length;
    state.oppDeckCount = state.oppTempDeck.filter(isInDeck).length;
  }

  function botFindEmptyOppSlot() {
    for (const i of FIELD_SLOT_ORDER) {
      if (state.oppField[i] === null) return i;
    }
    return -1;
  }

  // Simplified player-side spendResources() — no preferEx option (none of
  // Deck 01's cards care which pool pays), no onExResourceSpent hook firing
  // (that hook's registered handlers are all player-hardcoded per this
  // file's header comment, and no Deck 01 card uses it anyway).
  function botSpendResources(cost) {
    if (!cost || cost <= 0) return;
    const fromRes = Math.min(state.oppRes, cost);
    state.oppRes -= fromRes;
    const remaining = cost - fromRes;
    if (remaining > 0) state.oppExRes = Math.max(0, state.oppExRes - remaining);
  }

  // Level is normally recomputed inside render() for the player
  // (state.playerLevel = resBase + exRes + exResRested) — there is no
  // opponent-side equivalent since real networked play never needs one (the
  // peer's own client tracks their own level). The bot has no such client,
  // so this must be called any time oppRes/oppExRes/oppExResRested changes.
  function recomputeOppLevel() {
    state.oppLevel = (state.oppResBase || 0) + (state.oppExRes || 0) + (state.oppExResRested || 0);
  }

  function botCanPlayCard(card) {
    const level = parseInt(card.level) || 0;
    if ((state.oppLevel || 0) < level) return false;
    const cost = effectiveHandCost(card);
    if (cost > (state.oppRes + state.oppExRes)) return false;
    if (card.canPilot) return true; // pairs onto a unit rather than needing its own slot
    if (card.type === 'base') return true; // bases never occupy a field slot
    return botFindEmptyOppSlot() !== -1;
  }

  // ──────────────────────────────────────────────────────────────────────────
  // START/DRAW/RESOURCE/END PHASE — bot-side mirrors of
  // registerAction('UNREST_ALL'/'RESET_RESOURCES'/'GAIN_RESOURCE'/
  // 'REPAIR_FIELD', ...) (game.js), targeting oppField/oppBase/oppRes
  // instead of playerField/playerBase/res. Deliberately simplified: skips
  // the few passive-hook/NET-mirroring side-effects those originals also
  // fire (e.g. ST14-001-style "lowest-level unrest block" hooks, pilot HP-
  // recovery triggers) — none of Deck 01's cards use them, and a human deck
  // with such a card played against the bot just won't see that specific
  // interaction honored. Flagged here rather than silently, for whoever
  // extends this for a future deck that does need one.
  // ──────────────────────────────────────────────────────────────────────────
  function botUnrestAll() {
    state.oppField.forEach(c => {
      if (!c) return;
      if (!c.keepRestedNextTurn) c.rested = false;
      c.keepRestedNextTurn = false;
      c.tempKeywords = []; c.tempApMod = 0; c.battleApMod = 0;
      c.tempCantAttack = false; c.tempCantReceiveEffectDamage = false; c.tempCantAttackNonUnit = false;
      c.tempCantDirectAttack = false; c.tempCantBlock = false; c.tempImmuneToEffectDestruction = false;
      c.battleDefMod = 0; c.tempDestroyOnHit = null; c.nextDamageReduction = 0;
      c.tempGrantedAbilities = []; c.tempAttackDespiteSickness = false;
      c.tempIncomingDamageReduction = 0; c.tempBattleDamageReduction = 0;
      c.passiveUsedThisTurn = false; c.activateMainUsedThisTurn = false; c.activateActionUsedThisTurn = false;
      if (c.pilot) { c.pilot.passiveUsedThisTurn = false; c.pilot.activateMainUsedThisTurn = false; c.pilot.pilotEffectUsedThisTurn = false; }
    });
    if (state.oppBase) {
      state.oppBase.rested = false;
      state.oppBase.baseAbilityUsedThisTurn = false;
      state.oppBase.baseActionUsedThisTurn = false;
      state.oppBase.passiveUsedThisTurn = false;
    }
  }

  function botResetResources() {
    state.oppRes = state.oppResBase;
    if (state.oppExResRested > 0) {
      state.oppExRes += state.oppExResRested;
      state.oppExResRested = 0;
    }
    recomputeOppLevel();
  }

  function botGainResource() {
    if (state.oppResBase >= 10) return;
    state.oppRes = Math.min(state.oppRes + 1, 10);
    state.oppResBase = Math.min(state.oppResBase + 1, 10);
    recomputeOppLevel();
  }

  function botRepairField() {
    state.oppField.forEach(card => {
      if (!card) return;
      const repairVal = getRepairValue(card);
      if (repairVal <= 0) return;
      const entry = getCardDbEntry(card.img);
      const maxHp = entry ? (parseStatValue(entry.hp) ?? 0) : 0;
      const currentHp = parseInt(card.hp) || 0;
      if (currentHp >= maxHp) return;
      const newHp = Math.min(currentHp + repairVal, maxHp);
      card.hp = newHp;
      if (newHp >= maxHp) card.damaged = false;
    });
  }

  function botHandLimitDiscard() {
    if (state.oppHand.length <= 10) return;
    const overflow = state.oppHand.length - 10;
    // No deck-authored discard preference for Deck 01 (its curve rarely
    // overflows hand size) — default heuristic keeps the highest-cost cards.
    const toDiscard = [...state.oppHand]
      .sort((a, b) => (parseInt(a.cost) || 0) - (parseInt(b.cost) || 0))
      .slice(0, overflow);
    toDiscard.forEach(c => {
      state.oppHand = state.oppHand.filter(h => h.uid !== c.uid);
      state.oppTrashCount++;
      state.oppTrash.push(toTrashCard(c));
    });
    state.oppHandCount = state.oppHand.length;
    addLog(`${botName()} discarded ${toDiscard.length} card(s) to trash (hand limit).`);
  }

  // ──────────────────────────────────────────────────────────────────────────
  // DEPLOY / PAIR / ACTIVATE PRIMITIVES — bot-side mirrors of
  // registerAction('PLAY_CARD'/'DEPLOY_BASE'/'PAIR_PILOT'/'ACTIVATE_BASE'/
  // 'ACTIVATE_COMMAND', ...). Each does the same generic bookkeeping as the
  // real action, but calls the active deck's own bespoke onDeploy/onPair/
  // onActivateBase/onActivateCommand/onDeployBase hook afterward instead of
  // triggerCardEffect/window.CardEffects — see this file's header comment
  // for why. A cardNo with no matching hook simply has no extra effect
  // beyond the generic placement, same "no handler = no programmatic effect"
  // convention js/effects/registry.js documents for the real registry.
  // ──────────────────────────────────────────────────────────────────────────
  function botDeployUnit(uid) {
    const card = state.oppHand.find(c => c.uid === uid);
    if (!card) return null;
    const slot = botFindEmptyOppSlot();
    if (slot === -1) return null; // field full — bot simply skips this play (no trash-to-make-room in V1)
    const cost = effectiveHandCost(card);
    state.oppField[slot] = card;
    botSpendResources(cost);
    card.tempCantAttack = true; // deploy sickness — cleared by next botUnrestAll()
    state.oppHand = state.oppHand.filter(c => c.uid !== uid);
    state.oppHandCount = state.oppHand.length;
    const entry = state.oppTempDeck?.find(e => e.cardNo === card.cardNo && e.copyVer === card.copyVer);
    if (entry) entry.state = 'field';
    addLog(`${botName()} — played ${card.name}.`);
    render(); setupDropZones();
    activeDeck()?.onDeploy?.[card.cardNo]?.(card, buildBotCtx());
    render(); setupDropZones();
    return card;
  }

  function botDeployBase(uid) {
    const card = state.oppHand.find(c => c.uid === uid);
    if (!card) return null;
    botSpendResources(effectiveHandCost(card));
    state.oppHand = state.oppHand.filter(c => c.uid !== uid);
    state.oppHandCount = state.oppHand.length;
    const oldBase = state.oppBase;
    if (oldBase && !isExBaseCard(oldBase)) {
      state.oppTrashCount++;
      state.oppTrash.push(toTrashCard(oldBase));
    }
    const entry = state.oppTempDeck?.find(e => e.cardNo === card.cardNo && e.copyVer === card.copyVer);
    if (entry) entry.state = 'base';
    state.oppBase = card;
    applyOppBaseDeployEffect();
    addLog(`${botName()} — set ${card.name} as base.`);
    render(); setupDropZones();
    activeDeck()?.onDeployBase?.[card.cardNo]?.(card, buildBotCtx());
    render(); setupDropZones();
    return card;
  }

  // Mirrors game.js's deployBaseFromShield() — a Base card revealed from the
  // bot's own shield zone (its "[Burst]Deploy this card." text) is always
  // deployed, same convention as botDeployBase above (old base trashed
  // unless EX, generic shield-to-hand effect, then any bespoke onDeployBase
  // hook). `picked` is the tempDeck entry for the revealed card — its state
  // is already 'shield' at this point; resolveShieldHit (game.js) leaves it
  // to this function to move to 'base'.
  function deployBaseFromShield(card, picked) {
    const oldBase = state.oppBase;
    if (oldBase && !isExBaseCard(oldBase)) {
      state.oppTrashCount++;
      state.oppTrash.push(toTrashCard(oldBase));
    }
    if (picked) picked.state = 'base';
    state.oppBase = card;
    applyOppBaseDeployEffect();
    addLog(oldBase
      ? `${card.name} replaced ${oldBase.name} as base (from shield).`
      : `${card.name} deployed as base (from shield).`);
    render(); setupDropZones();
    activeDeck()?.onDeployBase?.[card.cardNo]?.(card, buildBotCtx());
    render(); setupDropZones();
  }

  // Mirrors applyBaseDeployEffect() (game.js) — "add 1 of your Shields to
  // your hand" — against the bot's own real oppShield/oppTempDeck/oppHand.
  function applyOppBaseDeployEffect() {
    if (state.oppShield <= 0) return;
    state.oppShield = Math.max(0, state.oppShield - 1);
    if (!state.oppTempDeck) return;
    const shields = state.oppTempDeck.filter(e => e.state === 'shield');
    if (shields.length === 0) return;
    const picked = shields[Math.floor(Math.random() * shields.length)];
    if (state.oppHand.length >= 10) { picked.state = 'trash'; state.oppTrashCount++; return; }
    picked.state = 'hand';
    const dbEntry = cardDb[picked.cardNo];
    if (dbEntry) {
      state.oppHand.push(buildGameCardFromDb(dbEntry, picked.copyVer));
      state.oppHandCount = state.oppHand.length;
    }
  }

  function botActivateBaseAbility() {
    const base = state.oppBase;
    if (!base) return;
    const cost = getBaseActivationCost(base);
    if (!cost) return;
    if (cost.res) botSpendResources(cost.res);
    if (cost.restBase) base.rested = true;
    if (cost.oncePerTurn) base.baseAbilityUsedThisTurn = true;
    addLog(`${botName()} — activated ${base.name}'s effect.`);
    render(); setupDropZones();
    activeDeck()?.onActivateBase?.[base.cardNo]?.(base, buildBotCtx());
    render(); setupDropZones();
  }

  function botActivateCommand(uid) {
    const card = state.oppHand.find(c => c.uid === uid);
    if (!card) return;
    botSpendResources(effectiveHandCost(card));
    state.oppHand = state.oppHand.filter(c => c.uid !== uid);
    state.oppHandCount = state.oppHand.length;
    const entry = state.oppTempDeck?.find(e => e.cardNo === card.cardNo && e.copyVer === card.copyVer);
    if (entry) entry.state = 'trash';
    state.oppTrashCount++;
    state.oppTrash.push(toTrashCard(card));
    addLog(`${botName()} — activated ${card.name} (sent to trash).`);
    render(); setupDropZones();
    activeDeck()?.onActivateCommand?.[card.cardNo]?.(card, buildBotCtx());
    render(); setupDropZones();
  }

  // Mirrors registerAction('PAIR_PILOT', ...) (game.js), which fires the
  // 'pair' trigger on BOTH the pilot's own cardNo AND the unit's own cardNo
  // (a [When Paired] ability can be printed on either card — e.g. GD03-001
  // Gundam NT-1's own "[When Paired] Choose 1 rested enemy Unit..." fires
  // off the UNIT's registration, js/effects/blue.js's gd03001PairEffect).
  // ctx carries pairedUnit/pilot so a deck's hook (keyed by either card's
  // cardNo) can use the same (selfCard, ctx) signature either way.
  function botPairPilot(pilotUid, unitUid) {
    const pilot = state.oppHand.find(c => c.uid === pilotUid);
    const slot = state.oppField.findIndex(c => c && c.uid === unitUid);
    if (!pilot || slot === -1) return;
    const unitCard = state.oppField[slot];
    botSpendResources(parseInt(pilot.cost) || 0);
    const pairedUnit = { ...unitCard, pilot };
    if (!unitCard.pilot && isLinkUnit(pairedUnit)) {
      pairedUnit.linkedThisTurn = true;
      pairedUnit.tempCantAttack = false; // linking removes deploy sickness
    }
    state.oppField[slot] = pairedUnit;
    state.oppHand = state.oppHand.filter(c => c.uid !== pilotUid);
    state.oppHandCount = state.oppHand.length;
    const entry = state.oppTempDeck?.find(e => e.cardNo === pilot.cardNo && e.copyVer === pilot.copyVer);
    if (entry) entry.state = 'field';
    addLog(isLinkUnit(pairedUnit)
      ? `${pilot.name} links as pilot to ${unitCard.name}.`
      : `${pilot.name} paired as pilot to ${unitCard.name}.`);
    render(); setupDropZones();
    const deck = activeDeck();
    const pairCtx = Object.assign(buildBotCtx(), { pairedUnit, pilot: pairedUnit.pilot });
    deck?.onPair?.[pilot.cardNo]?.(pairedUnit.pilot, pairCtx);
    deck?.onPair?.[pairedUnit.cardNo]?.(pairedUnit, pairCtx);
    render(); setupDropZones();
  }

  function deployTokenToOppField(cardNo) {
    const entry = cardDb[cardNo];
    if (!entry) return null;
    const slot = botFindEmptyOppSlot();
    if (slot === -1) return null; // field full — bot skips (no trash-to-make-room in V1)
    const copyVer = `token-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
    const token = buildGameCardFromDb(entry, copyVer);
    state.oppField[slot] = token;
    token.tempCantAttack = true;
    render(); setupDropZones();
    return token;
  }

  // Mirrors NET.onUnitToDeckBottom's body (game.js) — "return THIS unit to
  // the bottom of its owner's deck." Only correct/usable when `target` is on
  // state.playerField (the human): their hand/tempDeck ARE state.hand/
  // state.tempDeck on this same client (there's no second client to ask in a
  // bot match), so this can resolve fully and correctly — unlike the
  // player-side bounceEnemyUnitToDeckBottom() (js/effects/white.js), which
  // can only ever tell a REAL peer what to do via NET.send and would
  // silently no-op here.
  function botReturnPlayerUnitToDeckBottom(target) {
    const idx = state.playerField.findIndex(c => c && c.uid === target.uid);
    if (idx === -1) return;
    state.playerField[idx] = null;
    if (target.pilot) {
      const dbPilot = cardDb[target.pilot.cardNo];
      const pilotHandCard = dbPilot
        ? buildGameCardFromDb(dbPilot, target.pilot.copyVer)
        : { ...target.pilot, rested: false, damaged: false };
      updateTempState(target.pilot.cardNo, target.pilot.copyVer, 'hand');
      state.hand.push(pilotHandCard);
      glowHandCard(pilotHandCard.uid);
    }
    if (!isUnitToken(target) && state.tempDeck) {
      const entry = state.tempDeck.find(e => e.cardNo === target.cardNo && e.copyVer === target.copyVer);
      if (entry) { entry.state = 'deck'; sendDeckEntryToBottom(entry); }
    }
    broadcastCounters();
  }

  // Same shape as botReturnPlayerUnitToDeckBottom above, but for a bot
  // effect that returns a HUMAN-owned unit to the human's own HAND instead
  // of the bottom of their deck (e.g. "Choose 1 enemy Unit Lv.3 or lower.
  // Return it to its owner's hand." — the player-side handler for this calls
  // bounceEnemyUnit(), which always operates on state.oppField/oppHand, the
  // WRONG direction for a bot-owned copy of the same effect targeting the
  // human). Same "target is on state.playerField, so their hand/tempDeck ARE
  // this client's real data" reasoning — fully resolvable with no second
  // client involved.
  function botBouncePlayerUnitToHand(target) {
    const idx = state.playerField.findIndex(c => c && c.uid === target.uid);
    if (idx === -1) return;
    state.playerField[idx] = null;
    if (target.pilot) {
      const dbPilot = cardDb[target.pilot.cardNo];
      const pilotHandCard = dbPilot
        ? buildGameCardFromDb(dbPilot, target.pilot.copyVer)
        : { ...target.pilot, rested: false, damaged: false };
      updateTempState(target.pilot.cardNo, target.pilot.copyVer, 'hand');
      state.hand.push(pilotHandCard);
      glowHandCard(pilotHandCard.uid);
    }
    if (!isUnitToken(target)) {
      const dbEntry = cardDb[target.cardNo];
      const handCard = dbEntry
        ? buildGameCardFromDb(dbEntry, target.copyVer)
        : { ...target, pilot: null, rested: false, damaged: false };
      updateTempState(target.cardNo, target.copyVer, 'hand');
      state.hand.push(handCard);
      glowHandCard(handCard.uid);
    }
    broadcastCounters();
    render();
    setupDropZones();
  }

  // Mirrors NET.onUnitBounce's body (game.js) — the OPPOSITE direction from
  // botReturnPlayerUnitToDeckBottom above: a HUMAN card's effect
  // (bounceEnemyUnit, js/effects/white.js) bounced a BOT-OWNED unit back to
  // hand. That function already removes the card from state.oppField itself
  // (it has to, to update the human's own view of the bot's board) and then
  // calls this to do what a real peer's own NET.onUnitBounce handler would —
  // CONFIRMED BUG found via live play: without this, bounceEnemyUnit's
  // NET.send({type:'UNIT_BOUNCE',...}) silently no-ops in a bot match (no
  // socket to send on), so the card simply vanished — never reaching
  // oppHand at all, let alone being reconsidered by
  // chooseNextDeployOrActivate on a later turn. Tokens never go to hand
  // (removed from the game entirely, same as the real handler); a paired
  // Pilot always returns to hand as its own card.
  function botReceiveBouncedUnit(card) {
    if (card.pilot) {
      const dbPilot = cardDb[card.pilot.cardNo];
      const pilotHandCard = dbPilot
        ? buildGameCardFromDb(dbPilot, card.pilot.copyVer)
        : { ...card.pilot, rested: false, damaged: false };
      const pilotEntry = state.oppTempDeck?.find(e => e.cardNo === card.pilot.cardNo && e.copyVer === card.pilot.copyVer);
      if (pilotEntry) pilotEntry.state = 'hand';
      state.oppHand.push(pilotHandCard);
    }
    if (!isUnitToken(card)) {
      const dbEntry = cardDb[card.cardNo];
      const handCard = dbEntry ? buildGameCardFromDb(dbEntry, card.copyVer) : { ...card, pilot: null, rested: false, damaged: false };
      const entry = state.oppTempDeck?.find(e => e.cardNo === handCard.cardNo && e.copyVer === handCard.copyVer);
      if (entry) entry.state = 'hand';
      state.oppHand.push(handCard);
      addLog(`${card.name}${card.pilot ? ` and ${card.pilot.name} were` : ' was'} returned to ${botName()}'s hand.`);
    } else if (card.pilot) {
      addLog(`${card.name} (token) is removed from the game. ${card.pilot.name} returned to ${botName()}'s hand.`);
    }
    state.oppHandCount = state.oppHand.length;
    render();
    setupDropZones();
  }

  // Same bot-has-no-second-client gap as botReceiveBouncedUnit above, but for
  // "return to the bottom of its owner's deck" effects (bounceEnemyUnitToDeckBottom,
  // js/effects/white.js) instead of "return to hand" — a paired Pilot still
  // returns to hand (same convention as the bounce-to-hand case), but the
  // Unit itself rejoins the bottom of the bot's own oppTempDeck via its
  // already-tracked entry rather than becoming a fresh hand card.
  function botReceiveUnitToDeckBottom(card) {
    if (card.pilot) {
      const dbPilot = cardDb[card.pilot.cardNo];
      const pilotHandCard = dbPilot
        ? buildGameCardFromDb(dbPilot, card.pilot.copyVer)
        : { ...card.pilot, rested: false, damaged: false };
      const pilotEntry = state.oppTempDeck?.find(e => e.cardNo === card.pilot.cardNo && e.copyVer === card.pilot.copyVer);
      if (pilotEntry) pilotEntry.state = 'hand';
      state.oppHand.push(pilotHandCard);
    }
    if (!isUnitToken(card) && state.oppTempDeck) {
      const entry = state.oppTempDeck.find(e => e.cardNo === card.cardNo && e.copyVer === card.copyVer);
      if (entry) {
        const idx = state.oppTempDeck.indexOf(entry);
        state.oppTempDeck.splice(idx, 1);
        entry.state = 'deck';
        state.oppTempDeck.push(entry);
      }
    }
    addLog(card.pilot
      ? `${card.name} was returned to the bottom of ${botName()}'s deck; ${card.pilot.name} was returned to ${botName()}'s hand.`
      : `${card.name} was returned to the bottom of ${botName()}'s deck.`);
    state.oppHandCount = state.oppHand.length;
    render();
    setupDropZones();
  }

  // ──────────────────────────────────────────────────────────────────────────
  // MAIN PHASE DRIVER — repeatedly asks the active deck what to do next
  // until it says nothing (null), then moves to attacks. Capped at
  // MAX_MAIN_PHASE_ACTIONS so a future deck's buggy decision function can
  // never hang a match in an infinite loop.
  // ──────────────────────────────────────────────────────────────────────────
  const MAX_MAIN_PHASE_ACTIONS = 30;
  // A literal 3 real seconds between bot actions/attacks (quality-of-life
  // pacing) — deliberately NOT divided by PHASE_SPEED (game.js) the way the
  // phase-transition-banner delays below are: PHASE_SPEED only exists as a
  // dev speed-up knob for game.js's own OWN delays, and dividing by it here
  // would just cancel back out to a constant anyway (3000*PHASE_SPEED,
  // divided by PHASE_SPEED again, is always 3000) — so there's nothing
  // gained by writing it that way, only a confusing extra multiply/divide.
  // A plain top-level const (not a function) is safe here specifically
  // because it's a pure number literal with no game.js global reference —
  // unlike referencing PHASE_SPEED at this file's top level, which would
  // execute immediately (this whole file is an IIFE that runs the instant
  // the script loads, BEFORE game.js has loaded at all — see index.html's
  // script order) and throw ReferenceError right there, silently aborting
  // this entire IIFE before it ever reaches `return { ... }` and leaving
  // window.BotEngine undefined, breaking every bot match outright.
  const PER_ACTION_DELAY_MS = 3000;

  // deck.attacksBeforeDeploys (Deck 01 sets this — "before playing anything,
  // attack the shield area first when possible") runs an attack pass BEFORE
  // the deploy loop instead of after, then a SECOND attack pass afterward —
  // pairing a Link pilot onto a Unit deployed this same turn removes its
  // deploy sickness (e.g. Amuro Ray onto a freshly-played Gundam, or Char
  // Aznable onto Z'Gok), so it can still attack this turn even though it
  // wasn't on the field for the first pass. botTakeAttacks already only
  // ever attacks with units that are still eligible (!cardCantAttackNow),
  // so the second pass is a no-op whenever nothing newly qualified.
  //
  // deck.deferFirstAttackPass(ctx) (optional) lets a deck SKIP that
  // pre-deploy attack pass entirely for a specific turn. Added for Deck 01's
  // Gundam ST01-001 + Amuro Ray combo: confirmed via live testing that
  // without this, a unit already on the field (e.g. Kapool) would spend the
  // first attack pass chipping the enemy base BEFORE Gundam was even
  // deployed — directly undermining the whole point of the combo (solo the
  // base with one pair-boosted unit so everything else goes straight for
  // the shield area). When this returns true, the ENTIRE main phase runs as
  // deploy-loop-then-single-attack-pass instead, same as a non-
  // attacksBeforeDeploys deck would.
  function runBotMainPhase(onDone) {
    const deck = activeDeck();
    const deferFirstPass = deck?.attacksBeforeDeploys && deck?.deferFirstAttackPass?.(buildBotCtx());
    if (deck?.attacksBeforeDeploys && !deferFirstPass) {
      botTakeAttacks(() => runDeployLoop(deck, () => botTakeAttacks(onDone)));
    } else {
      runDeployLoop(deck, () => botTakeAttacks(onDone));
    }
  }

  function runDeployLoop(deck, onDone) {
    let iterations = 0;
    function step() {
      if (isMatchOver()) return;
      if (++iterations > MAX_MAIN_PHASE_ACTIONS) { onDone(); return; }
      const action = deck?.chooseNextDeployOrActivate?.(buildBotCtx());
      if (!action) { onDone(); return; }
      switch (action.kind) {
        case 'deploy':          botDeployUnit(action.uid); break;
        case 'deployBase':      botDeployBase(action.uid); break;
        case 'activateBase':    botActivateBaseAbility(); break;
        case 'activateCommand': botActivateCommand(action.uid); break;
        case 'pairPilot':       botPairPilot(action.pilotUid, action.unitUid); break;
        default: break;
      }
      botDelay(PER_ACTION_DELAY_MS, step);
    }
    botDelay(PER_ACTION_DELAY_MS, step);
  }

  // ──────────────────────────────────────────────────────────────────────────
  // ATTACK DRIVER — dispatch('ATTACK', {..., mine:false}) for each attacker
  // the deck chooses to swing with, in the order it chooses, against the
  // target it chooses. Reuses the REAL combat engine (see header comment),
  // so blocking/damage/triggers for whichever side owns each card are
  // handled exactly as they would be for a real second player.
  // ──────────────────────────────────────────────────────────────────────────
  function botTakeAttacks(onDone) {
    const deck = activeDeck();
    const ctx = buildBotCtx();
    let attackers = state.oppField.filter(c => c && !cardCantAttackNow(c));
    // chooseAttackOrder returns the EXACT ordered list of uids that should
    // attack this turn — anything it leaves out (e.g. Zock under its
    // holdback condition) simply doesn't attack, it isn't appended back in.
    // Returning null/undefined keeps the default "attack with everything
    // eligible, field order" behavior.
    if (deck?.chooseAttackOrder) {
      const chosenUids = deck.chooseAttackOrder({ ...ctx, attackers }) || [];
      const byUid = new Map(attackers.map(c => [c.uid, c]));
      attackers = chosenUids.map(u => byUid.get(u)).filter(Boolean);
    }

    function next() {
      if (isMatchOver()) return;
      // Re-filter every step — an earlier attack this same loop can change
      // eligibility (a counter-damage destroy, a now-gone shield/base, etc).
      attackers = attackers.filter(c => state.oppField.includes(c) && !cardCantAttackNow(c));
      if (attackers.length === 0) { onDone(); return; }
      const attacker = attackers.shift();
      const targets = getValidAttackTargets(attacker, 'opp');
      if (targets.length === 0) { next(); return; }
      const target = deck?.chooseAttackTarget?.(attacker, targets, ctx) || targets[0];
      dispatch('ATTACK', {
        attackerUid: attacker.uid,
        defenderUid: target.uid,
        isShield: !!target.isShield,
        isDirect: !!target.isDirect,
        mine: false,
      });
      waitForAttackToSettle(next);
    }
    // Gate the very first attack of this pass too — e.g. the human might
    // still have an Action Step picker open from whatever just preceded this
    // call (the prior deploy loop's last action, or the pre-deploy attack
    // pass finishing right as a modal opened).
    waitUntilClearToAct(next);
  }

  // Waits for state.attackInProgress to clear (runEpilogue clears it once
  // the whole Block/Action/Damage sequence for this attack finishes), THEN
  // for no human modal to be open, before calling `cb` — attacks that open a
  // human block-prompt or an Action Step picker can take an arbitrarily long
  // time to resolve, so this polls rather than using a fixed delay.
  function waitForAttackToSettle(cb) {
    const poll = () => {
      if (isMatchOver()) return;
      if (state.attackInProgress) { setTimeout(poll, 150); return; }
      botDelay(PER_ACTION_DELAY_MS, cb);
    };
    setTimeout(poll, 150);
  }

  // ──────────────────────────────────────────────────────────────────────────
  // TURN DRIVER — the bot has no second client to run its own
  // startNewTurn()/advanceNewTurn() (game.js) for itself, so this is a full
  // parallel Start→Draw→Resource→Main→End sequence, paced with the same
  // PHASE_SPEED-scaled delays those use. Ends by replicating NET.onEndTurn's
  // body (game.js) — the exact hand-off a real peer's END_TURN message would
  // trigger — so control returns to the human's own existing turn-start flow
  // completely unmodified.
  // ──────────────────────────────────────────────────────────────────────────
  // Index into state.phases: ['Start Phase','Draw Phase','Resource Phase','Main Phase','End Phase'].
  // Each step here mirrors NET.onPhaseSync's own body (game.js) — the real
  // handler a peer's PHASE_SYNC message drives — dispatch('SET_PHASE', ...)
  // first (updates state.phase/phaseIndex, which is what the phase bar UI
  // actually reads), THEN log/announce, THEN render. Previously this only
  // called showPhaseAnnouncement() (the fading banner) without ever touching
  // state.phase itself, so the phase bar stayed stuck showing whatever the
  // human's own last phase was (usually "End Phase", right where they just
  // handed off) for the bot's entire turn.
  function setBotPhase(index, label) {
    dispatch('SET_PHASE', { index });
    addLog(`Phase → ${label}`);
    showPhaseAnnouncement(label);
    render();
    setupDropZones();
  }

  function startBotTurn() {
    dispatch('NEW_TURN'); // side-agnostic: just increments state.turn + logs

    setTimeout(() => {
      botUnrestAll();
      botResetResources();
      setBotPhase(0, 'Start Phase');
    }, 1800 / PHASE_SPEED);

    setTimeout(() => {
      const drawn = botDrawCards(1);
      state.oppHand.push(...drawn);
      state.oppHandCount = state.oppHand.length;
      setBotPhase(1, 'Draw Phase');
    }, 4000 / PHASE_SPEED);

    setTimeout(() => {
      botGainResource();
      setBotPhase(2, 'Resource Phase');
    }, 6000 / PHASE_SPEED);

    setTimeout(() => {
      waitUntilClearToAct(() => {
        setBotPhase(3, 'Main Phase');
        runBotMainPhase(endBotTurn);
      });
    }, 8000 / PHASE_SPEED);
  }

  // Mirrors advanceThroughPhases() (game.js) — the human's own "I'm going
  // first, this is turn 1" flow — for the case where the coin flip (see
  // initGame(), game.js) hands the BOT the first turn instead. Turn 1 is
  // special on both sides: state.turn is already 1 (set directly in
  // initGame, not via a NEW_TURN dispatch) and there's nothing on the field
  // yet to unrest/reset, so — exactly like advanceThroughPhases() — this
  // skips both the NEW_TURN turn-counter increment and the Start Phase
  // entirely and goes straight to Draw→Resource→Main. startBotTurn() above
  // is NOT reused for this: it unconditionally calls dispatch('NEW_TURN')
  // and runs a real Start Phase (UNREST_ALL/RESET_RESOURCES), both of which
  // are only correct from the bot's second turn onward.
  function startBotFirstTurn() {
    setTimeout(() => {
      setBotPhase(1, 'Draw Phase');
    }, 2000 / PHASE_SPEED);

    setTimeout(() => {
      const drawn = botDrawCards(1);
      state.oppHand.push(...drawn);
      state.oppHandCount = state.oppHand.length;
    }, 2500 / PHASE_SPEED);

    setTimeout(() => {
      botGainResource();
      setBotPhase(2, 'Resource Phase');
    }, 4000 / PHASE_SPEED);

    setTimeout(() => {
      waitUntilClearToAct(() => {
        setBotPhase(3, 'Main Phase');
        runBotMainPhase(endBotTurn);
      });
    }, 6000 / PHASE_SPEED);
  }

  function endBotTurn() {
    if (isMatchOver()) return;
    botDelay(PER_ACTION_DELAY_MS, () => {
      if (isMatchOver()) return;
      setBotPhase(4, 'End Phase');
      botRepairField();
      dispatch('CLEAR_TURN_EFFECTS'); // already side-agnostic (clears both fields)
      botHandLimitDiscard();
      addLog(`${botName()}'s turn has ended.`, { noPill: true });
      state.myTurn = true;
      showTurnIndicator();
      render();
      setupDropZones();
      startNewTurn(); // hands off to the human exactly like a real peer's END_TURN would
    });
  }

  // ──────────────────────────────────────────────────────────────────────────
  // DECISION SEAMS — called from game.js at the exact points a dummy/
  // disconnected opponent used to just no-op. See this file's header comment
  // for the full list of call sites.
  // ──────────────────────────────────────────────────────────────────────────

  // The HUMAN is attacking; does the BOT block? Mirrors runBlockStep's own
  // ctx.defenderSide === 'player' branch (game.js) one level down — same
  // eligibility computation, against state.oppField instead of
  // state.playerField, decided by the deck instead of a human click.
  function decideBlock(ctx, next) {
    const deck = activeDeck();
    const original = ctx.defender;
    const cantBlockCeiling = window.CardEffects?.[ctx.attacker.cardNo]?.cantBlockLevelCeiling?.(ctx.attacker) ?? -1;
    const eligible = hasNativeKeyword(ctx.attacker, 'High-Maneuver') ? [] : state.oppField.filter(c =>
      c && !c.rested && c.uid !== original.uid && hasNativeKeyword(c, 'Blocker') && !c.tempCantBlock &&
      effectiveHp(c) > 0 && (parseInt(c.level) || 0) > cantBlockCeiling);
    if (eligible.length === 0) { next(); return; }
    const willing = deck?.shouldBlock ? eligible.filter(c => deck.shouldBlock(c, ctx)) : eligible;
    if (willing.length === 0) { next(); return; }
    const blockerUid = deck?.chooseBlockTarget?.(willing, ctx) ?? willing[0].uid;
    applyBlockDecision(ctx, blockerUid);
    next();
  }

  // Reactive Action-Step plays during combat — Deck 01 has none (no card in
  // it carries an [Action] trigger), so this is a documented stub for a
  // future deck. Not currently wired into game.js's runActionStep at all:
  // that function's existing `else if (!state.networked) next();` dummy
  // fallback already produces the correct "the bot plays nothing" behavior
  // for a bot match (bot matches are also networked:false), so there is
  // nothing to intercept yet. Exposed here so a future deck's
  // chooseActionStepPlay hook has somewhere real to be wired into once one
  // is needed.
  function decideActionStep(ctx, next) {
    // No bot-side resolveActionPlay/resolveUnitActionActivate-equivalent
    // exists yet — wire one up here, consulting the active deck's
    // chooseActionStepPlay(ctx), when the first deck that needs it is added.
    next();
  }

  // ──────────────────────────────────────────────────────────────────────────
  // PICKER-MODAL FALLBACK — see game.js's isBotControlledCard guard inside
  // showCardPickerModal/showCardPickerModalMulti. In practice this file's
  // own onDeploy/onPair/... hooks resolve every one of Deck 01's choices
  // directly (no modal ever opens for a bot-owned card), so this mostly
  // exists as a safety net for any call path this feature didn't anticipate,
  // or a future deck's bot_logic file that doesn't bother writing a bespoke
  // handler for every choice point.
  // ──────────────────────────────────────────────────────────────────────────
  function resolvePick(title, cards, opts, activatorCard) {
    const deck = activeDeck();
    const result = deck?.resolvePick?.(title, cards, opts, activatorCard, buildBotCtx());
    if (result !== undefined) return Array.isArray(result) ? (result[0] ?? null) : result;
    return cards.length > 0 ? cards[0] : null;
  }

  function resolvePickMulti(title, cards, opts, activatorCard) {
    const deck = activeDeck();
    const result = deck?.resolvePick?.(title, cards, opts, activatorCard, buildBotCtx());
    if (result !== undefined) return Array.isArray(result) ? result : (result ? [result] : []);
    const n = Math.max(opts?.min || 0, opts?.mandatory ? 1 : 0);
    return cards.slice(0, n);
  }

  return {
    setupBotSide,
    startBotTurn,
    startBotFirstTurn,
    decideBlock,
    decideActionStep,
    resolvePick,
    resolvePickMulti,
    deployBaseFromShield, // called from game.js's resolveShieldHit
    // Exposed for deck files (js/bot_logic/decks/*.js) to call from their
    // onDeploy/onPair/onActivateBase/... hooks, and for console/debugging use.
    botCanPlayCard,
    botFindEmptyOppSlot,
    botReturnPlayerUnitToDeckBottom,
    botBouncePlayerUnitToHand,
    recomputeOppLevel, // call after any direct oppExRes/oppExResRested mutation (e.g. a mirrored placeExResource)
    botReceiveBouncedUnit,
    botReceiveUnitToDeckBottom,
    deployTokenToOppField,
    botDrawCards,
  };
})();
